/**
 * Сессии для аналитики (ISSUE-68): длина, число за день. Сессия кончается перерывом {@link SESSION_GAP_MS}, короткое
 * сворачивание её продолжает. Время идёт только пока игра на экране.
 *
 * Уходящего игрока надо успеть посчитать, поэтому `session_end` шлётся при каждом сворачивании с нарастающим `seconds`:
 * длина сессии — максимум `seconds` по `sid`. В Telegram на Android закрытие часто проходит без события, поэтому
 * состояние лежит в хранилище и обновляется пульсом; неотправленный хвост уходит при следующем открытии.
 */
import type { AnalyticsEvent } from "@newfungame/contracts";
import { dayNumber } from "./day";

/** Как у визита Umami. */
export const SESSION_GAP_MS = 30 * 60 * 1000;
export const BEAT_MS = 15_000;
/** Больше за один шаг не считаем: заморозка WebView бывает без события. */
export const MAX_STEP_MS = 60_000;
const KEY = "nfg.a.session";

export interface SessionDeps {
  track(event: AnalyticsEvent): void;
  now(): number;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  newId(): string;
}

interface Saved {
  sid: string;
  /** Номер сессии за календарный день `date`. */
  n: number;
  date: number;
  /** Время на экране. */
  ms: number;
  waves: number;
  /** Последняя отметка времени. */
  at: number;
  /** Какое `ms` уже ушло в `session_end`. */
  sentMs: number;
}

function isSaved(v: unknown): v is Saved {
  const s = v as Partial<Saved> | null;
  return (
    typeof s === "object" &&
    s !== null &&
    typeof s.sid === "string" &&
    [s.n, s.date, s.ms, s.waves, s.at, s.sentMs].every((x) => typeof x === "number")
  );
}

export function createSessions(deps: SessionDeps) {
  let session: Saved | null = null;
  let createdAt = 0;
  let onScreen = true;
  let stepFrom = deps.now();

  const load = (): Saved | null => {
    try {
      const raw = deps.storage?.getItem(KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return isSaved(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };
  const save = (): void => {
    try {
      deps.storage?.setItem(KEY, JSON.stringify(session));
    } catch {
      /* хранилище недоступно — сессия живёт в памяти */
    }
  };
  /** Прибавить время на экране с прошлого шага. */
  const step = (): void => {
    const now = deps.now();
    if (session) {
      if (onScreen) session.ms += Math.min(Math.max(0, now - stepFrom), MAX_STEP_MS);
      session.at = now;
    }
    stepFrom = now;
  };
  const sendEnd = (s: Saved): void => {
    if (s.ms <= s.sentMs) return;
    s.sentMs = s.ms;
    const { sid, n, waves } = s;
    deps.track({ name: "session_end", props: { sid, n, seconds: Math.round(s.ms / 1000), waves } });
  };
  /** Новая сессия после `prev`; время с `stepFrom` входит в неё. */
  const open = (prev: Saved | null): void => {
    if (prev) sendEnd(prev);
    const now = deps.now();
    const date = dayNumber(now);
    session = {
      sid: deps.newId(),
      n: prev?.date === date ? prev.n + 1 : 1,
      date,
      ms: onScreen ? Math.max(0, now - stepFrom) : 0,
      waves: 0,
      at: now,
      sentMs: 0,
    };
    stepFrom = now;
    const { sid, n } = session;
    deps.track({ name: "session_start", props: { sid, n, day: Math.max(0, date - dayNumber(createdAt)) } });
    save();
  };

  return {
    /** Известен игрок: сессия продолжается или начинается новая. `accountCreatedAt` — для дня с создания аккаунта. */
    begin(accountCreatedAt: number): void {
      if (session) return;
      createdAt = accountCreatedAt;
      const prev = load();
      if (prev && deps.now() - prev.at < SESSION_GAP_MS) {
        session = prev;
        step();
        save();
        return;
      }
      open(prev);
    },
    /** Игра свёрнута. */
    hidden(): void {
      if (!onScreen) return;
      step();
      onScreen = false;
      if (!session) return;
      sendEnd(session);
      save();
    },
    /** Игра снова на экране. */
    visible(): void {
      if (onScreen) return;
      onScreen = true;
      const away = session ? deps.now() - session.at : 0;
      stepFrom = deps.now();
      if (!session) return;
      if (away >= SESSION_GAP_MS) open(session);
      else save();
    },
    /** Пульс: пока игра на экране, держит состояние свежим на случай закрытия без события. */
    beat(): void {
      if (!onScreen || !session) return;
      step();
      save();
    },
    /** Волна окончена; вернуть секунды сессии. */
    wave(): number {
      step();
      if (!session) return 0;
      session.waves += 1;
      save();
      return Math.round(session.ms / 1000);
    },
  };
}

export type Sessions = ReturnType<typeof createSessions>;
